import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Button, Input, Form, InputNumber, DatePicker, Pagination, Spin, Tooltip, Tag, Table, Grid } from 'antd';
import { Link } from 'react-router-dom';
import {
  FiSearch,
  FiPlus,
  FiChevronDown,
  FiRefreshCw,
  FiLayers,
  FiBox,
  FiCheckCircle,
} from 'react-icons/fi';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import DashboardPageWrapper from '../../../components/layout/DashboardPageWrapper';
import productService from '../../../services/productService';
import fakeInventoryService from '../../../services/fakeInventoryService';
import { useAuth } from '../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../config/permissions';

const PRODUCTS_LIMIT = 10;
const LOTS_LIMIT = 10;

function apiErrMsg(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    'Request failed'
  );
}

function inr(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(n);
}

function qty(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n);
}

function summaryToStats(data) {
  if (!data || typeof data !== 'object') return [];
  const MAP = {
    total_original:      { label: 'Total Original',  accent: false },
    total_available:     { label: 'Available',        accent: true  },
    total_fake_available:{ label: 'Fake Available',   accent: true  },
    total_hold:          { label: 'On Hold',          accent: false },
    total_confirmed:     { label: 'Confirmed',        accent: false },
  };
  return Object.entries(data)
    .filter(([key]) => key !== 'product_id')
    .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
    .map(([key, value]) => ({
      key,
      label: MAP[key]?.label ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      value: String(value),
      accent: MAP[key]?.accent ?? false,
    }));
}

/* ─── skeletons ─── */

function RowSkeleton() {
  return (
    <div className="animate-pulse border border-gray-100 rounded-2xl p-4 flex items-center gap-4 bg-white">
      <div className="w-10 h-10 rounded-xl bg-gray-200 shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3.5 bg-gray-200 rounded w-2/5" />
        <div className="h-2.5 bg-gray-200 rounded w-1/4" />
      </div>
      <div className="flex gap-3">
        <div className="w-16 h-5 bg-gray-200 rounded-full" />
        <div className="w-16 h-5 bg-gray-200 rounded-full" />
      </div>
      <div className="w-5 h-5 bg-gray-200 rounded-full" />
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="px-5 pb-5 pt-0 space-y-5 animate-pulse">
      {/* stat strip */}
      <div className="flex gap-3 overflow-hidden">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="rounded-2xl bg-gray-100 h-[72px] w-32 shrink-0" />
        ))}
      </div>
      {/* lot cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-[140px] rounded-2xl bg-gray-100" />
        ))}
      </div>
    </div>
  );
}

/* ─── lots table ─── */

const LOT_COLUMNS = [
  {
    title: 'Price',
    dataIndex: 'supplier_price',
    key: 'supplier_price',
    width: 130,
    fixed: 'left',
    render: (v) => (
      <span className="font-semibold text-gray-900 tabular-nums">{inr(v)}</span>
    ),
  },
  {
    title: 'Inv. Date',
    dataIndex: 'inventory_date',
    key: 'inventory_date',
    width: 120,
    render: (d) => (
      <span className="text-gray-600">
        {d ? dayjs(d).format('DD MMM YYYY') : '—'}
      </span>
    ),
  },
  {
    title: 'Available',
    dataIndex: 'available_quantity',
    key: 'available_quantity',
    width: 100,
    align: 'right',
    render: (v) => (
      <span className="font-bold text-green-600 tabular-nums">{qty(Number(v) || 0)}</span>
    ),
  },
  {
    title: 'Original',
    dataIndex: 'original_quantity',
    key: 'original_quantity',
    width: 100,
    align: 'right',
    render: (v) => (
      <span className="text-gray-700 tabular-nums">{qty(Number(v) || 0)}</span>
    ),
  },
  {
    title: 'Hold',
    dataIndex: 'hold_quantity',
    key: 'hold_quantity',
    width: 80,
    align: 'right',
    render: (v) => (
      <span className="text-gray-500 tabular-nums">{qty(Number(v) || 0)}</span>
    ),
  },
  {
    title: 'Confirmed',
    dataIndex: 'confirmed_quantity',
    key: 'confirmed_quantity',
    width: 100,
    align: 'right',
    render: (v) => (
      <span className="text-gray-500 tabular-nums">{qty(Number(v) || 0)}</span>
    ),
  },
  {
    title: 'Remaining',
    key: 'remaining',
    width: 130,
    render: (_, record) => {
      const avail = Number(record.available_quantity) || 0;
      const orig  = Number(record.original_quantity)  || 0;
      const pct   = orig > 0 ? Math.round((avail / orig) * 100) : 0;
      return (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden min-w-[60px]">
            <div className="h-full rounded-full bg-green-400" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[11px] text-gray-400 tabular-nums w-8 text-right shrink-0">{pct}%</span>
        </div>
      );
    },
  },
  {
    title: 'Added By',
    dataIndex: 'added_by_name',
    key: 'added_by_name',
    width: 150,
    ellipsis: true,
    render: (v) => <span className="text-gray-500">{v || '—'}</span>,
  },
  {
    title: 'Created',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 130,
    render: (d) => (
      <span className="text-gray-400 text-xs">
        {d ? dayjs(d).format('DD MMM, HH:mm') : '—'}
      </span>
    ),
  },
];

function LotsTable({ lots }) {
  return (
    <Table
      rowKey={(r) => r.uuid || r.id}
      columns={LOT_COLUMNS}
      dataSource={lots}
      pagination={false}
      size="small"
      scroll={{ x: 'max-content' }}
      rowClassName="hover:bg-green-50/40 transition-colors"
      className="lots-table"
    />
  );
}

/* ─── add lot form ─── */

function AddLotForm({ productId, onSuccess, disabled }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const screens = Grid.useBreakpoint();
  const isLg = Boolean(screens.lg);

  const handleFinish = async (values) => {
    const inventory_date = values.inventory_date
      ? dayjs(values.inventory_date).format('YYYY-MM-DD')
      : null;
    if (!inventory_date) { toast.error('Pick an inventory date'); return; }
    setLoading(true);
    try {
      const res = await fakeInventoryService.addLot(productId, {
        supplier_price: values.supplier_price,
        quantity: values.quantity,
        inventory_date,
      });
      if (res?.success === false) { toast.error(res?.message || res?.error || 'Could not add lot'); return; }
      toast.success(res?.message || 'Lot added successfully');
      form.setFieldsValue({ supplier_price: undefined, quantity: undefined, inventory_date: dayjs() });
      onSuccess?.();
    } catch (err) {
      toast.error(apiErrMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-green-100 bg-green-50/30">
      {/* toggle header */}
      <button
        type="button"
        onClick={() => setFormOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-4 py-3 hover:bg-green-50/60 transition-colors"
      >
        <div className="w-5 h-5 rounded-md bg-green-100 flex items-center justify-center shrink-0">
          <FiPlus size={11} className="text-green-600" />
        </div>
        <span className="text-xs font-semibold text-gray-700">Add Fake Lot</span>
        <FiChevronDown
          size={13}
          className={`ml-auto text-gray-400 transition-transform duration-200 ${formOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* collapsible body */}
      {formOpen && (
      <div className="px-5 py-4 sm:px-6 sm:py-5 border-t border-green-100/80">
      <Form
        form={form}
        onFinish={handleFinish}
        disabled={disabled || loading}
        requiredMark={false}
        initialValues={{ inventory_date: dayjs() }}
        layout={isLg ? 'inline' : 'vertical'}
      >
        <div
          className={
            isLg
              ? 'flex flex-wrap items-end gap-x-3 gap-y-2'
              : 'flex flex-col gap-0'
          }
        >
          {/* price */}
          <Form.Item
            name="supplier_price"
            rules={[{ required: true, message: 'Enter price' }]}
            className={isLg ? '!mb-0' : '!mb-3 w-full max-w-full'}
            label={<span className="text-[11px] font-medium text-gray-500">Price (₹)</span>}
          >
            <InputNumber
              min={0}
              className={isLg ? '!w-40' : '!w-full'}
              style={{ width: isLg ? undefined : '100%' }}
              placeholder="3,200"
              formatter={(v) => (v ? `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
              parser={(v) => v.replace(/₹\s?|(,*)/g, '')}
              controls={false}
              size="middle"
            />
          </Form.Item>

          {/* quantity */}
          <Form.Item
            name="quantity"
            rules={[
              { required: true, message: 'Enter qty' },
              { type: 'number', min: 1, message: 'Min 1' },
            ]}
            className={isLg ? '!mb-0' : '!mb-3 w-full max-w-full'}
            label={<span className="text-[11px] font-medium text-gray-500">Qty (units)</span>}
          >
            <InputNumber
              min={1}
              precision={0}
              className={isLg ? '!w-40' : '!w-full'}
              style={{ width: isLg ? undefined : '100%' }}
              placeholder="20"
              controls={false}
              size="middle"
            />
          </Form.Item>

          {/* date */}
          <Form.Item
            name="inventory_date"
            rules={[{ required: true, message: 'Pick date' }]}
            className={isLg ? '!mb-0' : '!mb-3 w-full max-w-full'}
            label={<span className="text-[11px] font-medium text-gray-500">Inventory Date</span>}
          >
            <DatePicker
              className={isLg ? '!w-40' : 'w-full max-w-full'}
              style={isLg ? undefined : { width: '100%' }}
              format="DD MMM YYYY"
              size="middle"
              allowClear={false}
            />
          </Form.Item>

          {/* actions */}
          <Form.Item className={isLg ? '!mb-0 ml-auto' : '!mb-0'}>
            <div className={`flex items-center gap-2 ${isLg ? '' : 'w-full sm:w-auto'}`}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="middle"
                className={`font-semibold ${isLg ? '' : 'flex-1 sm:flex-none'}`}
              >
                {loading ? 'Adding…' : '+ Add Lot'}
              </Button>
              <Button
                htmlType="button"
                size="middle"
                disabled={loading}
                className={isLg ? '' : 'flex-1 sm:flex-none'}
                onClick={() =>
                  form.setFieldsValue({
                    supplier_price: undefined,
                    quantity: undefined,
                    inventory_date: dayjs(),
                  })
                }
              >
                Clear
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
      </div>
      )}
    </div>
  );
}

/* ─── expanded detail panel ─── */

function ProductDetail({ product, canEdit }) {
  const [summaryData, setSummaryData] = useState(null);
  const [allLots, setAllLots]         = useState([]);
  const [totalCount, setTotalCount]   = useState(0);
  const [hasMore, setHasMore]         = useState(false);
  const [nextPage, setNextPage]       = useState(2);
  const [loading, setLoading]         = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setAllLots([]);
    setHasMore(false);
    setNextPage(2);
    try {
      const [sRes, lRes] = await Promise.all([
        fakeInventoryService.getSummary(product.id),
        fakeInventoryService.getLots(product.id, { page: 1, limit: LOTS_LIMIT }),
      ]);
      if (sRes?.success !== false) setSummaryData(sRes?.data ?? null);
      else toast.error(sRes?.message || 'Failed to load summary');
      if (lRes?.success !== false) {
        const ld = lRes?.data ?? {};
        const fetched = Array.isArray(ld.lots) ? ld.lots : [];
        const lp = ld.pagination ?? {};
        const total = Number(lp.total) || 0;
        setAllLots(fetched);
        setTotalCount(total);
        setHasMore(fetched.length < total);
      } else {
        toast.error(lRes?.message || 'Failed to load lots');
      }
    } catch (err) {
      toast.error(apiErrMsg(err));
    } finally {
      setLoading(false);
    }
  }, [product.id]);

  useEffect(() => { load(); }, [load]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const lRes = await fakeInventoryService.getLots(product.id, { page: nextPage, limit: LOTS_LIMIT });
      if (lRes?.success !== false) {
        const ld = lRes?.data ?? {};
        const fetched = Array.isArray(ld.lots) ? ld.lots : [];
        setAllLots((prev) => [...prev, ...fetched]);
        setNextPage((p) => p + 1);
        const lp = ld.pagination ?? {};
        const total = Number(lp.total) || 0;
        setTotalCount(total);
        setHasMore(fetched.length > 0 && allLots.length + fetched.length < total);
      }
    } catch (err) {
      toast.error(apiErrMsg(err));
    } finally {
      setLoadingMore(false);
    }
  }, [product.id, nextPage, hasMore, loadingMore, allLots.length]);

  const { ref: sentinelRef, inView } = useInView({ rootMargin: '150px', threshold: 0 });

  useEffect(() => {
    if (inView && hasMore && !loadingMore && !loading) loadMore();
  }, [inView, hasMore, loadingMore, loading, loadMore]);

  const stats = useMemo(() => summaryToStats(summaryData), [summaryData]);

  return (
    <div className="border-t border-gray-100 bg-gray-50/40">
      {loading ? (
        <DetailSkeleton />
      ) : (
        <div className="px-5 py-5 space-y-5">

          {/* ── stat strip ── */}
          {stats.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {stats.map((s) => (
                <div
                  key={s.key}
                  className={`rounded-xl border px-4 py-3 min-w-[110px] ${
                    s.accent
                      ? 'bg-green-600 border-green-600 shadow-sm shadow-green-200'
                      : 'bg-white border-gray-200 shadow-sm'
                  }`}
                >
                  <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${s.accent ? 'text-green-100' : 'text-gray-400'}`}>
                    {s.label}
                  </div>
                  <div className={`text-xl font-extrabold tabular-nums ${s.accent ? 'text-white' : 'text-gray-900'}`}>
                    {s.value}
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={load}
                className="ml-auto self-start mt-0.5 p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white border border-transparent hover:border-gray-100 transition-all"
                title="Refresh"
              >
                <FiRefreshCw size={13} />
              </button>
            </div>
          )}

          {/* ── add lot form ── */}
          {canEdit ? (
            <AddLotForm productId={product.id} onSuccess={load} />
          ) : (
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              <FiCheckCircle size={12} className="text-gray-300" />
              View-only — inventory edit permission required to add lots.
            </p>
          )}

          {/* ── lots ── */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FiBox size={13} className="text-gray-400" />
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Inventory Lots
              </span>
              {totalCount > 0 && (
                <span className="text-[11px] text-gray-400">({totalCount})</span>
              )}
            </div>

            {allLots.length === 0 ? (
              <div className="flex flex-col items-center py-10 rounded-xl border border-dashed border-gray-200 text-center">
                <FiBox size={22} className="text-gray-300 mb-2" />
                <p className="text-sm text-gray-500 font-medium">No lots yet</p>
              </div>
            ) : (
              <>
                <LotsTable lots={allLots} />
                {hasMore && <div ref={sentinelRef} className="h-4" aria-hidden="true" />}
                {loadingMore && (
                  <div className="flex items-center justify-center gap-2 py-3">
                    <Spin size="small" />
                    <span className="text-xs text-gray-400">Loading more lots…</span>
                  </div>
                )}
                {!hasMore && allLots.length > 0 && allLots.length >= totalCount && totalCount > LOTS_LIMIT && (
                  <div className="text-center text-xs text-gray-300 py-2">All lots loaded</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── accordion row ─── */

function ProductAccordionRow({ record, isOpen, onToggle, canEdit }) {
  const fakeCnt = Number(record.total_fake_available) || 0;
  const realCnt = Number(record.total_real_available) || 0;

  return (
    <div
      className={`relative rounded-2xl border overflow-hidden transition-all duration-200 ${
        isOpen
          ? 'border-green-200 shadow-md shadow-green-50'
          : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'
      } bg-white`}
    >
      {/* left accent line spans full open accordion height */}
      {isOpen && (
        <span className="absolute left-0 top-0 bottom-0 w-1 bg-green-500 z-10" />
      )}

      {/* ── row header (always visible) ── */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:ring-offset-1"
      >

        {/* logo */}
        <div
          className={`w-10 h-10 rounded-xl shrink-0 overflow-hidden flex items-center justify-center border ${
            isOpen ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-100'
          }`}
        >
          {record.logo_url ? (
            <img src={record.logo_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[11px] font-bold text-gray-400">
              {(record.name || '?').slice(0, 2).toUpperCase()}
            </span>
          )}
        </div>

        {/* name / slug */}
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm font-semibold truncate ${isOpen ? 'text-green-800' : 'text-gray-900'}`}
            title={record.name}
          >
            {record.name}
          </p>
          <p className="text-[11px] text-gray-400 truncate mt-0.5">{record.slug || record.id}</p>
        </div>

        {/* fake / real — fixed widths keep columns aligned across all rows */}
        <div className="hidden sm:flex items-center gap-0 shrink-0 divide-x divide-gray-100">
          <Tooltip title="Fake available">
            <div className="flex flex-col items-end w-24 px-4">
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide leading-none mb-0.5">Fake</span>
              <span className={`text-sm font-bold tabular-nums leading-none ${fakeCnt > 0 ? 'text-green-600' : 'text-gray-300'}`}>
                {qty(fakeCnt)}
              </span>
            </div>
          </Tooltip>
          <Tooltip title="Real available">
            <div className="flex flex-col items-end w-24 px-4">
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide leading-none mb-0.5">Real</span>
              <span className={`text-sm font-semibold tabular-nums leading-none ${realCnt > 0 ? 'text-gray-700' : 'text-gray-300'}`}>
                {qty(realCnt)}
              </span>
            </div>
          </Tooltip>
        </div>

        {/* chevron */}
        <FiChevronDown
          size={16}
          className={`shrink-0 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-green-600' : ''}`}
        />
      </button>

      {/* ── expandable detail ── */}
      {isOpen && <ProductDetail product={record} canEdit={canEdit} />}
    </div>
  );
}

/* ─────────────── main page ─────────────── */

export default function FakeInventoryPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canEdit = isSuperAdmin || hasPermission(PERMISSIONS.PRODUCT_PRE_IPO_INVENTORY_EDIT);

  const [products, setProducts]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [openId, setOpenId]           = useState(null);
  const debounceRef                   = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchDebounced(searchInput.trim());
      setProductPage(1);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productService.getProducts({
        type: 'PRE_IPO',
        page: productPage,
        limit: PRODUCTS_LIMIT,
        search: searchDebounced,
      });
      if (res?.success === false) {
        toast.error(res?.message || 'Failed to load products');
        setProducts([]); setProductTotal(0);
        return;
      }
      const data = res?.data ?? {};
      setProducts(Array.isArray(data.products) ? data.products : []);
      setProductTotal(Number(data.pagination?.total) || 0);
    } catch (err) {
      toast.error(apiErrMsg(err));
      setProducts([]); setProductTotal(0);
    } finally {
      setLoading(false);
    }
  }, [productPage, searchDebounced]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <DashboardPageWrapper>
      <div className="max-w-[1200px] mx-auto">
        {/* ── breadcrumb ── */}
        <nav className="flex items-center gap-1 text-xs text-gray-400 mb-4">
          <Link to="/dashboard" className="hover:text-gray-700 transition-colors">Dashboard</Link>
          <span className="text-gray-300">/</span>
          <Link to="/pre-ipo/listings" className="hover:text-gray-700 transition-colors">Pre-IPO</Link>
          <span className="text-gray-300">/</span>
          <span className="text-gray-600 font-medium">Fake Inventory</span>
        </nav>

        {/* ── page header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">Fake Inventory</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Manage disposable lots per product — order requests are fulfilled from these until real inventory is confirmed.
            </p>
          </div>
          <Button
            icon={<FiRefreshCw size={13} />}
            onClick={fetchProducts}
            loading={loading}
            className="shrink-0 hidden sm:flex items-center gap-1.5"
          >
            Refresh
          </Button>
        </div>

        {/* ── search bar ── */}
        <div className="mb-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <FiSearch
              size={13}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <Input
              allowClear
              placeholder="Search products…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ paddingLeft: 32 }}
              size="middle"
            />
          </div>
          {!loading && (
            <span className="text-xs text-gray-400 whitespace-nowrap">{productTotal} products</span>
          )}
        </div>

        {/* ── product accordion list ── */}
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => <RowSkeleton key={i} />)}
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 rounded-2xl border border-dashed border-gray-200 bg-white text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mb-4">
              <FiLayers size={22} className="text-gray-400" />
            </div>
            <p className="font-semibold text-gray-700">No products found</p>
            <p className="text-sm text-gray-400 mt-1">Try a different search term.</p>
          </div>
        ) : (
          <div className="space-y-3 relative">
            {products.map((p) => (
              <ProductAccordionRow
                key={p.id}
                record={p}
                isOpen={openId === p.id}
                onToggle={() => toggle(p.id)}
                canEdit={canEdit}
              />
            ))}
          </div>
        )}

        {/* ── pagination ── */}
        {!loading && productTotal > PRODUCTS_LIMIT && (
          <div className="flex justify-center mt-6 pt-4 border-t border-gray-100">
            <Pagination
              current={productPage}
              pageSize={PRODUCTS_LIMIT}
              total={productTotal}
              showSizeChanger={false}
              onChange={(p) => { setProductPage(p); setOpenId(null); }}
              showTotal={(t) => <span className="text-xs text-gray-500">{t} products</span>}
            />
          </div>
        )}
      </div>
    </DashboardPageWrapper>
  );
}
